import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@chams-pay/design-system/lib/utils";

/*
 * Handoff spec: buttons are Cairo 700; the primary fill is teal-500 with a
 * teal-600 hover; disabled is teal-200 on teal-50 text (never opacity);
 * primary height 44px, secondary 40px; radius = field radius (8px).
 */
const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-md border border-transparent bg-clip-padding font-heading text-sm font-bold whitespace-nowrap transition-colors duration-base outline-none select-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none aria-invalid:border-destructive [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary-hover disabled:bg-teal-200 disabled:text-teal-50",
        outline:
          "border-input bg-card text-foreground hover:bg-surface-2 aria-expanded:bg-muted disabled:border-border disabled:text-disabled",
        secondary:
          "bg-accent text-accent-foreground hover:bg-teal-200/60 aria-expanded:bg-accent disabled:bg-sunken disabled:text-disabled",
        ghost:
          "hover:bg-sunken hover:text-foreground aria-expanded:bg-sunken aria-expanded:text-foreground disabled:text-disabled",
        destructive:
          "border-destructive-border bg-destructive-soft text-destructive hover:bg-[#fbeae8] focus-visible:outline-destructive disabled:bg-sunken disabled:text-disabled",
        "destructive-solid":
          "bg-destructive text-white hover:bg-[#9c211a] focus-visible:outline-destructive disabled:bg-[#e7d8d7] disabled:text-white",
        success:
          "bg-success text-success-foreground hover:bg-[#1a6a43] disabled:bg-success-border disabled:text-success-soft",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default:
          "h-11 gap-2 px-4 has-data-[icon=inline-end]:pe-3 has-data-[icon=inline-start]:ps-3",
        xs: "h-8 gap-1 px-2.5 text-xs [&_svg:not([class*='size-'])]:size-3",
        sm: "h-10 gap-1.5 px-3.5 text-[0.8rem] [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-12 gap-2 px-5 has-data-[icon=inline-end]:pe-3.5 has-data-[icon=inline-start]:ps-3.5",
        icon: "size-11",
        "icon-xs": "size-8 [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-10",
        "icon-lg": "size-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant = "default",
  size = "default",
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
  return (
    <ButtonPrimitive
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
